import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Customer } from '@/types'

interface CustomerComboboxProps {
  customers: Customer[]
  value: string // customer_id
  onChange: (customerId: string, customer: Customer | null) => void
  className?: string
}

interface DropdownPos {
  top: number
  left: number
  width: number
}

export function CustomerCombobox({ customers, value, onChange, className }: CustomerComboboxProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [dropdownPos, setDropdownPos] = useState<DropdownPos>({ top: 0, left: 0, width: 0 })
  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const selectedCustomer = customers.find((c) => c.id === value) || null

  const filtered = customers.filter((c) => {
    if (!search.trim()) return true
    const q = search.toLowerCase()
    return (
      c.name.toLowerCase().includes(q) ||
      c.phone.includes(q) ||
      (c.tax_id && c.tax_id.includes(q))
    )
  })

  const updatePos = useCallback(() => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    setDropdownPos({
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
      width: rect.width,
    })
  }, [])

  const handleOpen = () => {
    updatePos()
    setIsOpen(true)
  }

  useEffect(() => {
    if (!isOpen) return
    const handleScroll = () => updatePos()
    const handleResize = () => updatePos()
    const handleClickOutside = (e: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node) &&
        !(e.target as Element).closest('[data-customer-combobox-dropdown]')
      ) {
        setIsOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', handleResize)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleResize)
    }
  }, [isOpen, updatePos])

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchRef.current?.focus(), 50)
    }
  }, [isOpen])

  const handleSelect = (customer: Customer | null) => {
    onChange(customer?.id || '', customer)
    setIsOpen(false)
    setSearch('')
  }

  const dropdown = isOpen
    ? createPortal(
        <div
          data-customer-combobox-dropdown
          style={{
            position: 'absolute',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            zIndex: 9999,
          }}
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg"
        >
          {/* 搜尋欄 */}
          <div className="p-2 border-b border-gray-100 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                ref={searchRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="搜尋姓名 / 電話 / 統編..."
                className="w-full h-8 pl-7 pr-7 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded outline-none focus:ring-1 focus:ring-primary"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setIsOpen(false)
                    setSearch('')
                  }
                }}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>

          {/* 清單 */}
          <ul className="max-h-52 overflow-y-auto py-1">
            <li>
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className={cn(
                  'w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-400 italic',
                  !value && 'bg-gray-50 dark:bg-gray-700'
                )}
              >
                — 不指定客戶 —
              </button>
            </li>
            {filtered.length === 0 ? (
              <li className="px-3 py-4 text-sm text-center text-gray-400">找不到符合的客戶</li>
            ) : (
              filtered.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(c)}
                    className={cn(
                      'w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors',
                      c.id === value && 'bg-primary/5 dark:bg-primary/10'
                    )}
                  >
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-200">{c.name}</div>
                    <div className="text-xs text-gray-400 flex gap-3">
                      {c.phone && <span>{c.phone}</span>}
                      {c.tax_id && <span>統編：{c.tax_id}</span>}
                    </div>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>,
        document.body
      )
    : null

  return (
    <div className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (isOpen ? setIsOpen(false) : handleOpen())}
        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-white dark:bg-gray-800 px-3 text-sm shadow-sm outline-none cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        <span className={cn('truncate', selectedCustomer ? 'text-gray-900 dark:text-white' : 'text-muted-foreground')}>
          {selectedCustomer
            ? `${selectedCustomer.name}${selectedCustomer.phone ? ` — ${selectedCustomer.phone}` : ''}`
            : '選擇客戶'}
        </span>
        <ChevronDown className={cn('h-4 w-4 text-gray-400 shrink-0 ml-2 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {dropdown}
    </div>
  )
}

export default CustomerCombobox
